var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Box3, Vector3, Matrix4 } from "flavr-math";
import { mainScene } from "../mainScene";
import { renderer } from "../RendererGlobals";
import { DirectionalLight, Mesh, BasicShadowMap, PCFShadowMap, VSMShadowMap } from "three";
import { RendererSystem } from "../renderer/RendererSystem";
import { preferencesService } from "../../preferences/PreferencesService";
import { getQueryParameter } from "flavr-common";
import { MeshRenderable } from "../mesh/MeshRenderable";
const FRUSTUM_PADDING = 10;
const NEAR_CLIPPING_PLANE = -1;
const FAR_CLIPPING_PLANE = 500;
const tmpBox3 = new Box3();
const tmpVerts = [new Vector3(), new Vector3(), new Vector3(), new Vector3(), new Vector3(), new Vector3(), new Vector3(), new Vector3()];
export class ShadowSystem extends RendererSystem {
    constructor(rendererModule) {
        super(rendererModule, "ShadowSystem");
        this.needsUpdate = false;
        this.shadowCameraBoundingBox = new Box3();
        this.frustumBox = new Box3();
        this.inverseLightMatrixWorld = new Matrix4();
        this.needsUpdate = true;
        // this.update("2", false);
        preferencesService.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "shadowMapType")
                return false;
            this.needsUpdate = true;
            this.update(newValue, true);
            return true;
        }));
    }
    init() {
        const shadowMapType = getQueryParameter(window.location.href, "shadowMapType");
        if (!shadowMapType)
            this.needsUpdate = true;
        this.update(shadowMapType, true);
    }
    update(shadowMapType, updateMaterials) {
        if (!this.needsUpdate) {
            return;
        }
        if (!shadowMapType)
            shadowMapType = preferencesService.getPreferredValue("shadowMapType", "1");
        renderer.shadowMap.enabled = true;
        if (shadowMapType !== undefined) {
            switch (shadowMapType) {
                case "0":
                    renderer.shadowMap.type = BasicShadowMap;
                    break;
                case "1":
                    renderer.shadowMap.type = PCFShadowMap;
                    break;
                case "2":
                    renderer.shadowMap.type = VSMShadowMap;
                    break;
                case "3":
                    renderer.shadowMap.enabled = false;
                    break;
                default:
                    renderer.shadowMap.type = PCFShadowMap;
            }
        }
        else {
            renderer.shadowMap.type = PCFShadowMap;
        }
        const roomNode = mainScene;
        this.computeShadowCameraBoundingBox();
        roomNode.traverse((node) => {
            if (node instanceof DirectionalLight) {
                this.resizeShadowCameraFrustum(node);
            }
        });
        renderer.shadowMap.needsUpdate = true;
        this.needsUpdate = false;
        if (updateMaterials)
            mainScene.traverse((node) => {
                if (node instanceof Mesh) {
                    if (Array.isArray(node.material)) {
                        for (const m of node.material)
                            m.needsUpdate = true;
                    }
                    else {
                        node.material.needsUpdate = true;
                    }
                }
            });
    }
    // ToDo: move calculation to room manager
    computeShadowCameraBoundingBox() {
        this.shadowCameraBoundingBox.makeEmpty();
        for (const renderable of this.renderer.getRenderables(MeshRenderable)) {
            if (!renderable.owner.isModelInCurrentRoom())
                continue;
            if (!renderable.isCastShadow())
                continue;
            const geometryData = renderable.getGeometry();
            if (!geometryData)
                continue;
            if (!geometryData.geometry)
                continue;
            if (!geometryData.geometry.boundingBox) {
                geometryData.geometry.computeBoundingBox();
            }
            tmpBox3.copy(geometryData.geometry.boundingBox);
            tmpBox3.applyMatrix4(geometryData.mesh.matrixWorld);
            this.shadowCameraBoundingBox.union(tmpBox3);
        }
    }
    resizeShadowCameraFrustum(light) {
        this.frustumBox.makeEmpty();
        const min = this.shadowCameraBoundingBox.min;
        const max = this.shadowCameraBoundingBox.max;
        tmpVerts[0].set(min.x, min.y, min.z);
        tmpVerts[1].set(min.x, min.y, max.z);
        tmpVerts[2].set(min.x, max.y, min.z);
        tmpVerts[3].set(min.x, max.y, max.z);
        tmpVerts[4].set(max.x, min.y, min.z);
        tmpVerts[5].set(max.x, min.y, max.z);
        tmpVerts[6].set(max.x, max.y, min.z);
        tmpVerts[7].set(max.x, max.y, max.z);
        // light.update
        this.inverseLightMatrixWorld.copy(light.matrixWorld).invert();
        for (let i = 0; i < tmpVerts.length; i++) {
            tmpVerts[i].applyMatrix4(this.inverseLightMatrixWorld);
            this.frustumBox.expandByPoint(tmpVerts[i]);
        }
        const camera = light.shadow.camera;
        camera.left = this.frustumBox.min.x - FRUSTUM_PADDING;
        camera.right = this.frustumBox.max.x + FRUSTUM_PADDING;
        camera.bottom = this.frustumBox.min.y - FRUSTUM_PADDING;
        camera.top = this.frustumBox.max.y + FRUSTUM_PADDING;
        camera.near = this.frustumBox.min.z + NEAR_CLIPPING_PLANE;
        camera.far = this.frustumBox.max.z + FAR_CLIPPING_PLANE;
        camera.updateProjectionMatrix();
    }
}
